import { useMutation } from '@apollo/client';
import { kebabCase } from 'lodash';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import {
  AgendaAppointmentItemDataFragment,
  AppointmentRecurringTime,
  DeleteAgendaAppointmentItemDocument,
  GetAgendaItemsDocument,
} from '../../../../data/types/api/graphql';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import { InputCheckbox } from '../../../../elements/form/input/input-checkbox';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';

enum AgendaAppointmentDeleteType {
  Single = 'Single',
  Future = 'Future',
  All = 'All',
}

type AgendaAppointmentDeletePromptProps = {
  agendaAppointmentItem: AgendaAppointmentItemDataFragment;
  onDeleted?: (value: AgendaAppointmentItemDataFragment) => void;
  onClose?: () => void;
};

export function AgendaAppointmentDeletePrompt({
  agendaAppointmentItem,
  onDeleted,
  onClose,
}: AgendaAppointmentDeletePromptProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const [type, setType] = useState<AgendaAppointmentDeleteType>(
    AgendaAppointmentDeleteType.Single
  );

  const [deleteAgendaAppointmentItem] = useMutation(
    DeleteAgendaAppointmentItemDocument,
    {
      refetchQueries: [GetAgendaItemsDocument],
    }
  );

  const onHandleDelete = useCallback(async () => {
    try {
      const result = await deleteAgendaAppointmentItem({
        variables: {
          id: agendaAppointmentItem?.id ?? '',
          type: type as unknown as AppointmentRecurringTime,
        },
      });

      if (result.data?.appointmentDelete) {
        showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: intl.formatMessage({
            id: 'agenda.appointment-delete.success',
          }),
        });

        onDeleted?.(agendaAppointmentItem);

        onClose?.();
      }
    } catch (error) {
      showAlertMessage({
        type: AlertMessageType.ERROR,
        message: (error as Error).message,
      });
    }
  }, [
    intl,
    agendaAppointmentItem,
    type,
    deleteAgendaAppointmentItem,
    showAlertMessage,
    onDeleted,
    onClose,
  ]);

  if (!agendaAppointmentItem) return null;

  return (
    <ModalPrompt
      testId="agenda-appointment-delete"
      onConfirm={onHandleDelete}
      onClose={onClose}
    >
      <p>
        <CustomFormattedMessage id="agenda.appointment-delete.content" />
      </p>

      {agendaAppointmentItem.recurring && (
        <InputCheckbox<AgendaAppointmentDeleteType>
          name="type"
          value={type}
          options={Object.values(AgendaAppointmentDeleteType).map(option => ({
            key: option,
            title: intl.formatMessage({
              id: `agenda.appointment-delete.type.${kebabCase(option)}`,
            }),
          }))}
          onChange={value => setType(value as AgendaAppointmentDeleteType)}
        />
      )}
    </ModalPrompt>
  );
}
